import type { ChatLink } from '../types'
import CategoryBadge from './CategoryBadge'
import { displayUrl, twitterAuthor } from '../lib/displayUrl'
import { Tooltip } from './ui/Tooltip'

interface Props {
  link: ChatLink
  selected?: boolean
  onOpen: () => void
  onPreview: () => void
  onMarkRead?: () => void
  exiting?: boolean
  highlight?: boolean
}

export default function LinkCard({
  link,
  selected,
  onOpen,
  onPreview,
  onMarkRead,
  exiting,
  highlight,
}: Props) {
  const time = relativeTime(link.last_seen_at)
  const author = twitterAuthor(link)
  const shown = displayUrl(link)

  const senders = link.senders || []
  const lastSender = senders.length > 0 ? senders[senders.length - 1].username : null
  const uniqueUsernames = Array.from(new Set(senders.map((s) => s.username)))
  const otherCount = Math.max(0, uniqueUsernames.length - 1)
  const sendersTooltipNode =
    uniqueUsernames.length === 0 ? null : (
      <div className="space-y-0.5">
        <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
          Paylaşan
        </div>
        {uniqueUsernames.map((u) => (
          <div key={u}>@{u}</div>
        ))}
      </div>
    )

  const isRead = !!link.read_at

  return (
    <div
      className={[
        'group flex items-center gap-2.5 px-2.5 py-2 rounded-md border',
        selected
          ? 'bg-[var(--color-accent-soft)] border-[var(--color-accent)]/50'
          : highlight
            ? 'bg-amber-500/10 border-amber-500/40 hover:border-amber-500/60 hover:bg-amber-500/15'
            : 'bg-[var(--color-surface)] border-[var(--color-border)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-elevated)]',
        exiting ? 'opacity-0 translate-x-3 pointer-events-none' : 'opacity-100',
      ].join(' ')}
    >
      <button
        onClick={onOpen}
        className="flex-1 flex items-center gap-2.5 text-left min-w-0 cursor-pointer"
      >
        <CategoryBadge category={link.category} />
        <span className="text-xs text-[var(--color-text-muted)] shrink-0 font-mono">{link.domain}</span>
        {author && (
          <Tooltip content={highlight ? 'Önemli yazar' : 'Tweet sahibi'}>
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 border"
              style={
                highlight
                  ? {
                      color: 'rgb(252 211 77)',
                      background: 'rgba(245, 158, 11, 0.22)',
                      borderColor: 'rgba(245, 158, 11, 0.5)',
                    }
                  : {
                      color: 'var(--color-cat-twitter)',
                      background: 'color-mix(in oklab, var(--color-cat-twitter) 18%, transparent)',
                      borderColor: 'color-mix(in oklab, var(--color-cat-twitter) 50%, transparent)',
                    }
              }
            >
              {highlight && '★ '}@{author}
            </span>
          </Tooltip>
        )}
        {link.sender_count > 1 && (
          <Tooltip content={`Toplam ${link.sender_count} paylaşım`}>
            <span className="px-1.5 py-0.5 rounded bg-[var(--color-warning)]/15 text-[var(--color-warning)] text-[10px] font-bold shrink-0">
              ×{link.sender_count}
            </span>
          </Tooltip>
        )}
        <Tooltip content={link.url_original}>
          <span className="text-sm flex-1 truncate text-[var(--color-text)] group-hover:text-[var(--color-accent-hover)]">
            {shown}
          </span>
        </Tooltip>
        {lastSender && (
          <Tooltip content={sendersTooltipNode}>
            <span className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] shrink-0">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="truncate max-w-[140px]">{lastSender}</span>
              {otherCount > 0 && <span className="text-[var(--color-text-subtle)]">+{otherCount}</span>}
            </span>
          </Tooltip>
        )}
        <span className="text-xs text-[var(--color-text-subtle)] shrink-0 tabular-nums">{time}</span>
      </button>
      {!isRead && onMarkRead && (
        <Tooltip content="Okundu olarak işaretle (linki açmadan)">
          <button
            onClick={onMarkRead}
            className="shrink-0 px-2 py-1 rounded text-[var(--color-text-subtle)] hover:text-[var(--color-success)] hover:bg-[var(--color-success)]/10"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </button>
        </Tooltip>
      )}
      <Tooltip content="Önizleme">
        <button
          onClick={onPreview}
          className="shrink-0 px-2 py-1 rounded text-[var(--color-text-subtle)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      </Tooltip>
    </div>
  )
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 5_000) return 'yeni'
  if (ms < 60_000) return `${Math.floor(ms / 1000)}sn`
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}dk`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}sa`
  return `${Math.floor(ms / 86_400_000)}g`
}
